import {
  AutoCompleteAddress,
  DeviceInformation,
  MultiEmailInput,
} from '@/pages/new-rma/@x/rma-details';
import { CustomTextarea } from '@/shared/ui/components';
import { forwardRef } from 'react';

import { t } from 'ttag';
import { Col, Row } from 'antd';
import { useNewTicket } from './useNewTicket';
import type { NewTicketProps } from './newTicket.types';

/**
 * Renders the RMA ticket creation form for returning devices: device information, notification emails, clinic notes and shipping addresses. Takes state and change handlers from useNewTicket, seeds the device, sender and recipient sections from the original ticket data, blocks device edits for Protect Plus program tickets, and shows invalid-email errors on the email input.
 *
 * @param props - Country list, renewal stock flag and the ticket callbacks.
 * @param ref - Forwarded ref used by the RMA details page to read and submit the form.
 * @returns The RMA ticket form markup.
 */
const NewTicket = forwardRef((props: NewTicketProps, ref) => {
  const {
    state,
    emailRef,
    originalData,
    isSameAsShippingAddressRef,
    onChangeEmailList,
    onChangeInputText,
    onInputEmailChange,
    onChangeDataDeviceRMA,
    onChangeDivision,
    onChangeDataAddress,
    updateIsSameShippingAddress,
  } = useNewTicket(props, ref);

  return (
    <div className="rma-detail-content">
      <DeviceInformation
        selectedFacility={state.selectedFacility}
        onChangeDataDeviceRMA={onChangeDataDeviceRMA}
        initialRmaDevices={originalData.current?.initialRmaDevices}
        isNotEdit={state.isProtectPlusProgram}
        hasReplaceDevice={props.hasRenewalStock}
      />
      <Row className="rma-detail-content__row">
        <Col span={12} className="mr-3">
          <MultiEmailInput
            {...({} as any)}
            ref={emailRef}
            title={t`Notification email`}
            onChangeEmailList={onChangeEmailList}
            onInputChange={onInputEmailChange}
            isError={!!state.errorObject.invalidEmails}
            errorMessage={state.errorObject.invalidEmails}
          />
        </Col>
        <Col span={12} className="ml-3">
          <div className="notes-input">
            <div className="input-title">
              {t`Notes`}
              <span className="sub-title">{t`(optional)`}</span>
            </div>
            <CustomTextarea
              placeholder={t`Enter your message`}
              name="clinicNotes"
              onChange={onChangeInputText}
              value={state.clinicNotes}
            />
          </div>
        </Col>
      </Row>

      <div className="shipping-information">
        <div className="input-title">{t`Shipping Information`}</div>
        <AutoCompleteAddress
          {...({} as any)}
          countries={props.countries}
          onChangeDataAddress={onChangeDataAddress}
          onChangeDivision={onChangeDivision}
          errorObject={state.errorAddress}
          initialShippingAddress={originalData.current?.senderContact}
          initialReturningAddress={originalData.current?.recipientContact}
          isSameAsShippingAddress={isSameAsShippingAddressRef.current}
          updateIsSameShippingAddress={updateIsSameShippingAddress}
        />
      </div>
    </div>
  );
});

export default NewTicket;
